/**
 * ResetPasswordForm — forgot-password flow (email → code → new password)
 */

import React from "react";
import type { PasswordRequirement } from "../../config/passwordPolicy";
import PasswordRequirements from "./PasswordRequirements";

interface ResetPasswordFormProps {
  resetEmail: string;
  setResetEmail: (v: string) => void;
  resetCode: string;
  setResetCode: (v: string) => void;
  newPassword: string;
  setNewPassword: (v: string) => void;
  pendingConfirmPassword: string;
  setPendingConfirmPassword: (v: string) => void;
  resetStep: "email" | "code" | "reset";
  resetMessage: string;
  /**
   * How to present resetMessage. AL-472: supplied by the caller rather than
   * guessed from the wording, so the styling cannot contradict the message or
   * become a second, subtler way to tell outcomes apart.
   */
  resetMessageTone: "info" | "success" | "error";
  /** Requirements actually enforced by the pool, from checkPassword(). */
  passwordRequirements: PasswordRequirement[];
  onValidatePasswordRules: (pwd: string) => void;
  onForgotPassword: () => void;
  onConfirmForgotPassword: () => void;
  onBack: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  resetEmail,
  setResetEmail,
  resetCode,
  setResetCode,
  newPassword,
  setNewPassword,
  pendingConfirmPassword,
  setPendingConfirmPassword,
  resetStep,
  resetMessage,
  resetMessageTone,
  passwordRequirements,
  onValidatePasswordRules,
  onForgotPassword,
  onConfirmForgotPassword,
  onBack,
}) => (
  <>
    <h1>Reset Password</h1>
    {resetMessage && (
      <div
        className={
          resetMessageTone === "error"
            ? "error-message"
            : resetMessageTone === "success"
              ? "success-message"
              : "info-message"
        }
        data-testid="reset-message"
        data-tone={resetMessageTone}
        role={resetMessageTone === "error" ? "alert" : "status"}
      >
        {resetMessage}
      </div>
    )}

    {resetStep === "email" && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onForgotPassword();
        }}
        data-testid="reset-email-form"
      >
        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            placeholder="Enter your username"
            required
            data-testid="reset-email-input"
          />
        </div>
        <button
          type="submit"
          className="login-btn"
          data-testid="send-reset-code-btn"
        >
          Send Reset Code
        </button>
      </form>
    )}

    {resetStep === "code" && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onConfirmForgotPassword();
        }}
        data-testid="reset-code-form"
      >
        <div className="form-group">
          <label>Verification Code</label>
          <input
            type="text"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            placeholder="Enter code from email"
            required
            data-testid="reset-code-input"
          />
        </div>
        <div className="form-group">
          <label>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              onValidatePasswordRules(e.target.value);
            }}
            placeholder="Enter new password"
            required
            data-testid="reset-new-password-input"
          />
          {/* AL-470: rules now come from the configured policy rather than a
              hardcoded list, so the checklist cannot demand something the
              Cognito pool does not enforce. */}
          <PasswordRequirements requirements={passwordRequirements} />
        </div>
        <div className="form-group">
          <label>Confirm New Password</label>
          <input
            type="password"
            value={pendingConfirmPassword}
            onChange={(e) => setPendingConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            required
            data-testid="reset-confirm-password-input"
          />
        </div>
        <button
          type="submit"
          className="login-btn"
          data-testid="reset-password-submit-btn"
        >
          Reset Password
        </button>
      </form>
    )}

    <button
      type="button"
      className="forgot-password-btn"
      data-testid="back-to-login-from-reset-btn"
      onClick={onBack}
    >
      Back to Login
    </button>
  </>
);

export default ResetPasswordForm;
